<template>
  <!--
    "Do Not Sell or Share My Personal Information" landing page. CCPA
    requires this link to be conspicuous in the footer regardless of
    whether Kiey actually "sells" personal information. The page
    explains Kiey's stance and offers a one-click opt-out via the
    POST /v1/legal/do_not_sell endpoint.
  -->
  <div class="legal-page">
    <div class="legal-page-card">
      <div class="legal-page-header">
        <h1 class="legal-page-title">Do Not Sell or Share My Personal Information</h1>
      </div>

      <div class="dns-body">
        <p>
          Kiey does not sell personal information for monetary
          consideration. We also do not "share" personal information for
          cross-context behavioral advertising as that term is defined
          under the California Consumer Privacy Act (CCPA/CPRA).
        </p>
        <p>
          California's definition of "sharing" is broad. To opt out of
          any data sharing under that broad definition, click the button
          below. Your preference will be recorded immediately and
          honored across our platform.
        </p>
        <p>
          We also automatically honor browser-level Global Privacy
          Control (GPC) signals.
          <span v-if="gpc_detected" class="gpc-badge">GPC detected on your browser.</span>
          <span v-else class="gpc-badge gpc-badge-off">GPC not detected on your browser.</span>
        </p>

        <div class="dns-note">
          <strong>Cookie-level vs. account-level opt-out.</strong>
          If you opt out without an account, your preference is stored
          in your browser. If you're signed in, your preference is also
          attached to your Kiey account and applies on every device you
          use to sign in.
        </div>

        <div v-if="submitted" class="dns-success" role="status" aria-live="polite">
          <h2>Opt-out recorded</h2>
          <p>Tracking ID: <span class="tracking-id">{{ tracking_id }}</span></p>
          <p>Thanks. We've recorded your opt-out request. Your preference takes effect immediately.</p>
        </div>

        <form v-else class="dns-form" @submit.prevent="onSubmit">
          <label class="dns-label">
            Email address <span class="dns-required">*</span>
            <input v-model.trim="email" type="email" required class="dns-input" :disabled="submitting" />
          </label>
          <p v-if="error" class="dns-error" role="alert">{{ error }}</p>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? "Submitting…" : "Confirm opt-out" }}
          </button>
        </form>

        <hr />
        <p class="dns-fine-print">
          You can also email <a href="mailto:support@kiey.com">support@kiey.com</a> or use our
          <a href="/legal/privacy-request">privacy request form</a> to exercise this or any other right.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import config from "@/config/constants.js";

export default {
  name: "DoNotSell",
  data() {
    return {
      email: "",
      gpc_detected: false,
      submitting: false,
      submitted: false,
      tracking_id: "",
      error: "",
    };
  },
  computed: {
    current_user() {
      // Pre-fill the email from the active session if the user is
      // logged in. Falls back to anonymous opt-out if not.
      return (this.$store && this.$store.state && this.$store.state.Authenticate && this.$store.state.Authenticate.user) || null;
    },
  },
  mounted() {
    try {
      this.gpc_detected = !!(navigator && navigator.globalPrivacyControl);
    } catch (e) {
      this.gpc_detected = false;
    }
    if (this.current_user && this.current_user.email) {
      this.email = this.current_user.email;
    }
  },
  methods: {
    async onSubmit() {
      this.error = "";
      if (!this.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
        this.error = "Please enter a valid email address.";
        return;
      }
      this.submitting = true;
      try {
        const headers = {};
        try {
          const token = localStorage.getItem(config.token_key || "web-auth");
          if (token) headers.Authorization = token;
        } catch (e) {
          // localStorage unavailable (private mode, etc.) — no auth header.
        }
        const res = await axios.post(
          config.api_url + "legal/do_not_sell",
          {
            email: this.email,
            gpc_detected: this.gpc_detected,
            scope: "do_not_sell",
          },
          { headers }
        );
        const data = (res && res.data && res.data.data) || {};
        if (data.tracking_id) {
          this.tracking_id = data.tracking_id;
          this.submitted = true;
        } else {
          this.error = "Unexpected response. Please try again.";
        }
      } catch (e) {
        const status = e && e.response && e.response.status;
        if (status === 429) {
          this.error = "Too many submissions from this address. Please try again later.";
        } else {
          this.error = "Could not submit your opt-out. Please email support@kiey.com.";
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: #f5f7fb;
  padding: 32px 16px;
  box-sizing: border-box;
}
.legal-page-card {
  max-width: 720px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(6, 29, 71, 0.06);
  padding: 36px 40px;
}
.legal-page-header {
  border-bottom: 1px solid #e4e8f1;
  padding-bottom: 14px;
  margin-bottom: 22px;
}
.legal-page-title {
  color: #061d47;
  font-size: 24px;
  margin: 0;
}
.dns-body p {
  font-size: 14px;
  color: #2d2d2d;
  line-height: 1.6;
  margin: 0 0 14px;
}
.gpc-badge {
  display: inline-block;
  background: #e6f3ec;
  color: #1f8a4a;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 6px;
}
.gpc-badge-off { background: #f0f3f9; color: #6a7388; }
.dns-note {
  background: #f5f7fb;
  border-left: 3px solid #29488d;
  padding: 12px 16px;
  border-radius: 0 6px 6px 0;
  font-size: 13px;
  color: #2d2d2d;
  line-height: 1.5;
  margin: 4px 0 22px;
}
.dns-form { margin: 14px 0; }
.dns-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #4d566c;
  margin-bottom: 12px;
}
.dns-required { color: #c0392b; }
.dns-input {
  display: block;
  width: 100%;
  margin-top: 6px;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #d3d8e3;
  border-radius: 6px;
  box-sizing: border-box;
}
.dns-input:focus {
  outline: none;
  border-color: #29488d;
  box-shadow: 0 0 0 3px rgba(41, 73, 141, 0.12);
}
.dns-error {
  background: #fbecec;
  color: #b13c3c;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
}
.btn-primary {
  display: inline-block;
  background: #29488d;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border: 0;
  border-radius: 6px;
  padding: 12px 22px;
  cursor: pointer;
}
.btn-primary:hover { background: #1f3771; }
.btn-primary:disabled { background: #8b97b7; cursor: not-allowed; }
.dns-success {
  background: #e6f3ec;
  border-radius: 8px;
  padding: 18px 22px;
  margin: 14px 0 22px;
}
.dns-success h2 {
  color: #1f8a4a;
  font-size: 18px;
  margin: 0 0 8px;
}
.dns-success p { margin: 0 0 6px; }
.tracking-id {
  display: inline-block;
  background: #fff;
  color: #061d47;
  font-family: "SF Mono", Menlo, Monaco, Consolas, monospace;
  font-weight: 700;
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 4px;
}
.dns-fine-print {
  font-size: 12px;
  color: #6a7388;
  margin: 18px 0 0;
}
.dns-fine-print a { color: #29488d; }
hr { border: 0; border-top: 1px solid #e4e8f1; margin: 24px 0 18px; }
@media (max-width: 720px) {
  .legal-page-card { padding: 24px 18px; }
  .legal-page-title { font-size: 20px; }
}
</style>
